import React from 'react';

interface ToggleProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    size?: 'sm' | 'md' | 'lg';
    className?: string;
    id?: string;
    'data-cy'?: string;
}

export const Toggle: React.FC<ToggleProps> = ({
    checked,
    onChange,
    disabled = false,
    size = 'md',
    className = '',
    id,
    'data-cy': dataCy,
}) => {
    const handleClick = () => {
        if (!disabled) {
            onChange(!checked);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            handleClick();
        }
    };

    const sizes = {
        sm: {width: 32, height: 18, thumbSize: 14},
        md: {width: 44, height: 24, thumbSize: 20},
        lg: {width: 56, height: 30, thumbSize: 26}
    };

    const currentSize = sizes[size];

    const containerStyle: React.CSSProperties = {
        display: 'inline-block',
        cursor: disabled ? 'not-allowed' : 'pointer',
        userSelect: 'none',
        outline: 'none',
        opacity: disabled ? 0.6 : 1,
    };

    const trackStyle: React.CSSProperties = {
        position: 'relative',
        width: currentSize.width,
        height: currentSize.height,
        borderRadius: currentSize.height / 2,
        backgroundColor: checked ? (disabled ? '#6c757d' : '#009efb') : (disabled ? '#e9ecef' : '#ccc'),
        transition: 'background-color 0.2s ease',
    };

    const thumbStyle: React.CSSProperties = {
        position: 'absolute',
        top: 2,
        left: 2,
        width: currentSize.thumbSize,
        height: currentSize.thumbSize,
        borderRadius: '50%',
        backgroundColor: 'white',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
        transition: 'transform 0.2s ease',
        transform: checked ? `translateX(${(currentSize.width || 0) - (currentSize.thumbSize || 0) - 4}px)` : 'translateX(0)',
    };

    return (
        <div
            className={className}
            style={containerStyle}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            role="switch"
            aria-checked={checked}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            id={id}
            data-cy={dataCy}
        >
            <div style={trackStyle}>
                <div style={thumbStyle} />
            </div>
        </div>
    );
};
